import { useState, useEffect } from 'react';
import { Alert, Box, Link, Typography } from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { Link as RouterLink } from 'react-router-dom';
import { HINT_SEVERITY_MAP } from '../utils/hintEngine';

/**
 * Componente de Dica Contextual
 * Exibe dicas inteligentes baseadas no contexto da página como um
 * float fixo no canto inferior esquerdo da tela, sem bloquear o conteúdo.
 *
 * @param {object} hint - Objeto com { type, icon, message, action: { label, link } }
 * @param {function} onActionClick - Callback quando não há link (ex: abrir modal)
 */
export default function ContextualHint({ hint, onActionClick = null }) {
  const [dismissed, setDismissed] = useState(false);

  // Reexibe a dica sempre que o hint mudar (nova página ou novo hint)
  useEffect(() => {
    setDismissed(false);
  }, [hint]);

  if (!hint || dismissed) {
    return null;
  }

  const severity = HINT_SEVERITY_MAP[hint.type] || 'info';

  const accentColorBySeverity = {
    info: 'info.main',
    success: 'success.main',
    warning: 'warning.main',
    error: 'error.main',
  };

  const accentColor = accentColorBySeverity[severity] || 'info.main';

  const actionLabel = hint.action?.label;
  const actionLink = hint.action?.link;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        left: 24,
        zIndex: 1400,
        maxWidth: 480,
        width: 'calc(100vw - 48px)',
      }}
    >
      <Alert
        severity={severity}
        variant="outlined"
        icon={<LightbulbIcon sx={{ color: 'warning.main', fontSize: 18 }} />}
        onClose={() => setDismissed(true)}
        sx={{
          borderRadius: 2,
          bgcolor: 'background.paper',
          opacity: 0.93,
          boxShadow: 3,
          borderLeftWidth: 3,
          borderLeftStyle: 'solid',
          borderLeftColor: accentColor,
          py: 0.75,
          '& .MuiAlert-message': { width: '100%' },
          '& .MuiAlert-icon': { alignItems: 'center' },
          '& .MuiAlert-action': { alignItems: 'center', pt: 0 },
        }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={{
            lineHeight: 1.5,
            fontSize: '0.85rem',
          }}
        >
          {hint.icon && (
            <Typography
              component="span"
              sx={{
                mr: 0.5,
                fontSize: '1.1em',
                lineHeight: 1,
                verticalAlign: 'baseline',
              }}
            >
              {hint.icon}
            </Typography>
          )}
          <Typography component="span" sx={{ fontSize: '1.0em' }}>
            {hint.message}
          </Typography>
          {actionLabel && (actionLink || onActionClick) && (
            <>
              {' '}
              {actionLink ? (
                <Link component={RouterLink} to={actionLink} underline="always">
                  {actionLabel}
                </Link>
              ) : (
                <Link component="button" type="button" onClick={onActionClick} underline="always">
                  {actionLabel}
                </Link>
              )}
            </>
          )}
        </Typography>
      </Alert>
    </Box>
  );
}
